'use client';

import { useCallback, useEffect, useState } from 'react';

import { useEmblaCarousel } from '@/libs';

const TABLET_QUERY = '(min-width: 768px)';
const DESKTOP_QUERY = '(min-width: 1024px)';

type UseResultsCarouselParams = {
  totalSlides: number;
};

export const useResultsCarousel = ({ totalSlides }: UseResultsCarouselParams) => {
  const [visibleCount, setVisibleCount] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(0);

  const showControls = totalSlides > visibleCount;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    slidesToScroll: 1,
    watchDrag: showControls,
  });

  useEffect(() => {
    const tabletQuery = window.matchMedia(TABLET_QUERY);
    const desktopQuery = window.matchMedia(DESKTOP_QUERY);

    const updateVisibleCount = () => {
      if (desktopQuery.matches) {
        setVisibleCount(3);
      } else if (tabletQuery.matches) {
        setVisibleCount(2);
      } else {
        setVisibleCount(1);
      }
    };

    updateVisibleCount();
    tabletQuery.addEventListener('change', updateVisibleCount);
    desktopQuery.addEventListener('change', updateVisibleCount);

    return () => {
      tabletQuery.removeEventListener('change', updateVisibleCount);
      desktopQuery.removeEventListener('change', updateVisibleCount);
    };
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setSnapCount(emblaApi.scrollSnapList().length);
    };

    onSelect();
    emblaApi.on('select', onSelect).on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect).off('reInit', onSelect);
    };
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const canScrollPrev = selectedIndex > 0;
  const canScrollNext = selectedIndex < snapCount - 1;

  return {
    emblaRef,
    showControls,
    selectedIndex,
    snapCount,
    canScrollPrev,
    canScrollNext,
    scrollPrev,
    scrollNext,
    visibleCount,
  };
};
